// Journey Tracking Service
// Manages user progress, achievements, and learning analytics

import type {
  UserJourney,
  SerializedUserJourney,
  PageVisit,
  PageCategory,
  FrameworkInteraction,
  GlossaryProgress,
  IslandRegion,
  MountainElevation,
  MOFLayer,
  Achievement,
  AchievementId,
  Recommendation,
} from '../types/learning';
import {
  ACHIEVEMENT_DEFINITIONS,
  calculateTotalPoints,
  checkAchievementUnlock,
} from '../data/achievements';

const STORAGE_KEY = 'peak_priorities_user_journey';
const SESSION_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes

export class JourneyTracker {
  private journey: UserJourney;
  private currentPageStartTime: Date;

  constructor() {
    this.journey = this.loadJourney();
    this.currentPageStartTime = new Date();
    this.initializeSession();
  }

  // ===== INITIALIZATION =====

  private loadJourney(): UserJourney {
    try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        const serialized: SerializedUserJourney = JSON.parse(stored);
        return this.deserializeJourney(serialized);
      } catch (error) {
        console.error('Failed to load journey, creating new:', error);
          // Clear corrupted data
          try {
            localStorage.removeItem(STORAGE_KEY);
          } catch {
            // Ignore errors when removing
          }
        }
      }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing, unrelated to this change
    } catch (error: any) {
      // Handle quota errors or other localStorage issues
      if (error.name === 'QuotaExceededError' || error.code === 22) {
        console.warn('localStorage quota exceeded, starting fresh');
        // Try to clear and start fresh
        try {
          localStorage.removeItem(STORAGE_KEY);
        } catch {
          // Ignore errors
        }
      } else {
        console.error('Error accessing localStorage:', error);
      }
    }
    return this.createNewJourney();
  }

  private createNewJourney(): UserJourney {
    const now = new Date();
    return {
      userId: this.generateUserId(),
      createdAt: now,
      lastActive: now,
      pageVisits: [],
      currentSession: {
        startedAt: now,
        pagesVisited: 0,
        currentPath: '/',
      },
      frameworksViewed: [],
      glossaryTermsLearned: [],
      aiNavigatorStats: {
        totalQueries: 0,
        topicsExplored: [],
        helpfulResponses: 0,
        unhelpfulResponses: 0,
      },
      islandProgress: {
        visitedRegions: new Set(),
        regionDetails: new Map(),
      },
      mountainProgress: {
        currentElevation: 'base-camp',
        reachedElevations: new Set(['base-camp']),
        elevationDetails: new Map(),
      },
      mofProgress: {
        exploredLayers: new Set(),
        layerDetails: new Map(),
      },
      achievements: this.initializeAchievements(),
      totalAchievementPoints: 0,
      preferences: {
        focusAreas: [],
        learningPace: 'explorer',
        notificationPreferences: {
          achievements: true,
          recommendations: true,
          dailyTips: false,
        },
      },
      recommendedNext: [],
      learningStreak: {
        current: 1,
        longest: 1,
        lastVisit: now,
      },
      stats: {
        totalTimeSpent: 0,
        totalPageViews: 0,
        totalFrameworksViewed: 0,
        totalGlossaryTerms: 0,
        totalAIQueries: 0,
        averageSessionLength: 0,
      },
    };
  }

  private initializeAchievements(): Achievement[] {
    return Object.values(ACHIEVEMENT_DEFINITIONS).map(def => ({
      ...def,
      progress: 0,
    }));
  }

  private generateUserId(): string {
    return `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private initializeSession(): void {
    const now = new Date();
    const lastVisit = this.journey.lastActive;
    const timeSinceLastVisit = now.getTime() - lastVisit.getTime();

    if (timeSinceLastVisit > SESSION_TIMEOUT_MS) {
      // New session
      this.journey.currentSession = {
        startedAt: now,
        pagesVisited: 0,
        currentPath: '/',
      };
      this.updateLearningStreak();
    }

    this.journey.lastActive = now;
    this.saveJourney();
  }

  // ===== PAGE TRACKING =====

  trackPageVisit(path: string, category: PageCategory): void {
    const now = new Date();
    const previousPageDuration = Math.floor((now.getTime() - this.currentPageStartTime.getTime()) / 1000);

    // Update previous page duration
    if (this.journey.pageVisits.length > 0) {
      const lastVisit = this.journey.pageVisits[this.journey.pageVisits.length - 1];
      if (!lastVisit.durationSeconds) {
        lastVisit.durationSeconds = previousPageDuration;
      }
    }

    // Add new page visit
    const visit: PageVisit = {
      path,
      category,
      timestamp: now,
    };

    this.journey.pageVisits.push(visit);
    this.journey.currentSession.pagesVisited++;
    this.journey.currentSession.currentPath = path;
    this.journey.stats.totalPageViews++;

    this.currentPageStartTime = now;

    // Track MOF layer exploration
    // 'engines' maps to 'm4' (top of the m4→m3→m2→m1→m0 sequence below) —
    // this was passing the category name itself, which never matches
    // MOFLayer ('m4'|'m3'|'m2'|'m1'|'m0'), so m4 exploration was never
    // recorded.
    if (category === 'engines') this.exploreMOFLayer('m4');
    if (category === 'grammar') this.exploreMOFLayer('m3');
    if (category === 'models') this.exploreMOFLayer('m2');
    if (category === 'templates') this.exploreMOFLayer('m1');
    if (category === 'runtime') this.exploreMOFLayer('m0');

    // Track spatial metaphor engagement
    if (category === 'island') {
      this.checkAchievement('island-first-step', 1);
    }
    if (category === 'mountain') {
      this.checkAchievement('base-camp-arrival', 1);
    }

    this.saveJourney();
  }

  // ===== FRAMEWORK TRACKING =====

  trackFrameworkView(encodedId: string, name: string): void {
    const now = new Date();
    const existing = this.journey.frameworksViewed.find(f => f.encodedId === encodedId);

    if (existing) {
      // Update existing interaction
      const durationSeconds = Math.floor((now.getTime() - existing.viewedAt.getTime()) / 1000);
      existing.durationSeconds = durationSeconds;
      existing.depth = this.calculateDepth(durationSeconds);
    } else {
      // New framework view
      const interaction: FrameworkInteraction = {
        encodedId,
        name,
        viewedAt: now,
        depth: 'glance',
      };
      this.journey.frameworksViewed.push(interaction);
      this.journey.stats.totalFrameworksViewed++;
    }

    // Check framework-related achievements
    this.checkAchievement('framework-fan', this.journey.stats.totalFrameworksViewed);
    this.checkAchievement('model-master', this.journey.stats.totalFrameworksViewed);

    this.saveJourney();
  }

  private calculateDepth(seconds: number): FrameworkInteraction['depth'] {
    if (seconds < 30) return 'glance';
    if (seconds < 120) return 'scan';
    if (seconds < 1800) return 'study';
    return 'master';
  }

  trackFrameworkStudyTime(encodedId: string, seconds: number): void {
    const framework = this.journey.frameworksViewed.find(f => f.encodedId === encodedId);
    if (framework) {
      framework.durationSeconds = (framework.durationSeconds || 0) + seconds;
      framework.depth = this.calculateDepth(framework.durationSeconds);

      // Check deep dive achievement
      if (framework.durationSeconds >= 1800) { // 30 minutes
        this.checkAchievement('deep-diver', 1);
      }
    }
    this.saveJourney();
  }

  // ===== GLOSSARY TRACKING =====

  trackGlossaryTerm(term: string, conceptFamily?: string): void {
    const existing = this.journey.glossaryTermsLearned.find(g => g.term === term);
    if (existing) {
      existing.mastered = true; // Viewed again = reinforcement
    } else {
      const progress: GlossaryProgress = {
        term,
        viewedAt: new Date(),
        conceptFamily,
        mastered: false,
      };
      this.journey.glossaryTermsLearned.push(progress);
      this.journey.stats.totalGlossaryTerms++;
    }

    // Check glossary achievements
    this.checkAchievement('word-smith', this.journey.stats.totalGlossaryTerms);
    this.checkAchievement('polyglot', this.journey.stats.totalGlossaryTerms);

    this.saveJourney();
  }

  // ===== ISLAND PROGRESS =====

  visitIslandRegion(region: IslandRegion, relatedFrameworks: string[] = []): void {
    const { islandProgress } = this.journey;
    const now = new Date();

    if (!islandProgress.visitedRegions.has(region)) {
      islandProgress.visitedRegions.add(region);
      islandProgress.regionDetails.set(region, {
        firstVisit: now,
        lastVisit: now,
        totalTimeSeconds: 0,
        relatedFrameworks,
        mastered: false,
      });
    } else {
      const details = islandProgress.regionDetails.get(region)!;
      details.lastVisit = now;
      details.relatedFrameworks = [...new Set([...details.relatedFrameworks, ...relatedFrameworks])];
    }

    // Check island achievements
    this.checkAchievement('island-first-step', 1);
    this.checkAchievement('island-explorer', islandProgress.visitedRegions.size);

    this.saveJourney();
  }

  // ===== MOUNTAIN PROGRESS =====

  reachMountainElevation(elevation: MountainElevation): void {
    const { mountainProgress } = this.journey;
    const now = new Date();

    if (!mountainProgress.reachedElevations.has(elevation)) {
      mountainProgress.reachedElevations.add(elevation);
      mountainProgress.elevationDetails.set(elevation, {
        reachedAt: now,
        completedActivities: [],
        relatedFrameworks: [],
      });
    }

    mountainProgress.currentElevation = elevation;

    // Check mountain achievements
    if (elevation === 'summit') {
      this.checkAchievement('summit-seeker', 1);
    }
    this.checkAchievement('degap-master', mountainProgress.reachedElevations.size);

    this.saveJourney();
  }

  // ===== MOF PROGRESS =====

  exploreMOFLayer(layer: MOFLayer): void {
    const { mofProgress } = this.journey;
    const now = new Date();

    if (!mofProgress.exploredLayers.has(layer)) {
      mofProgress.exploredLayers.add(layer);
      mofProgress.layerDetails.set(layer, {
        firstVisit: now,
        lastVisit: now,
        itemsExplored: 0,
        mastered: false,
      });
    } else {
      const details = mofProgress.layerDetails.get(layer)!;
      details.lastVisit = now;
      details.itemsExplored++;
    }

    // Check MOF achievements
    this.checkAchievement('architect-apprentice', mofProgress.exploredLayers.size);

    this.saveJourney();
  }

  // ===== AI NAVIGATOR STATS =====

  trackAIQuery(topic?: string): void {
    this.journey.aiNavigatorStats.totalQueries++;
    this.journey.stats.totalAIQueries++;

    if (!this.journey.aiNavigatorStats.firstQuery) {
      this.journey.aiNavigatorStats.firstQuery = new Date();
    }
    this.journey.aiNavigatorStats.lastQuery = new Date();

    if (topic && !this.journey.aiNavigatorStats.topicsExplored.includes(topic)) {
      this.journey.aiNavigatorStats.topicsExplored.push(topic);
    }

    // Check AI engagement achievements
    this.checkAchievement('curious-learner', this.journey.aiNavigatorStats.totalQueries);
    this.checkAchievement('knowledge-seeker', this.journey.aiNavigatorStats.totalQueries);
    this.checkAchievement('power-user', this.journey.aiNavigatorStats.totalQueries);

    this.saveJourney();
  }

  // ===== ACHIEVEMENT SYSTEM =====

  private checkAchievement(id: AchievementId, currentProgress: number): void {
    const achievement = this.journey.achievements.find(a => a.id === id);
    if (!achievement) return;

    achievement.progress = Math.min(currentProgress, achievement.maxProgress);

    if (!achievement.unlockedAt && checkAchievementUnlock(achievement, currentProgress)) {
      this.unlockAchievement(achievement);
    }
  }

  private unlockAchievement(achievement: Achievement): void {
    achievement.unlockedAt = new Date();
    this.journey.totalAchievementPoints = calculateTotalPoints(this.journey.achievements);

    // Show notification (if enabled)
    if (this.journey.preferences.notificationPreferences.achievements) {
      this.showAchievementNotification(achievement);
    }

    // Check for completionist achievement
    const totalAchievements = this.journey.achievements.length - 1; // Exclude completionist itself
    const unlockedCount = this.journey.achievements.filter(a => a.unlockedAt && a.id !== 'completionist').length;
    if (unlockedCount === totalAchievements) {
      const completionist = this.journey.achievements.find(a => a.id === 'completionist');
      if (completionist && !completionist.unlockedAt) {
        completionist.unlockedAt = new Date();
        completionist.progress = 1;
        this.showAchievementNotification(completionist);
      }
    }
  }

  private showAchievementNotification(achievement: Achievement): void {
    // This will be implemented in the UI layer
    console.log(`🎉 Achievement Unlocked: ${achievement.icon} ${achievement.name}`);
    
    // Dispatch custom event for UI to catch
    const event = new CustomEvent('achievement-unlocked', {
      detail: achievement,
    });
    window.dispatchEvent(event);
  }

  // ===== LEARNING STREAK =====

  private updateLearningStreak(): void {
    const now = new Date();
    const lastVisit = this.journey.learningStreak.lastVisit;
    const daysSinceLastVisit = Math.floor((now.getTime() - lastVisit.getTime()) / (24 * 60 * 60 * 1000));

    if (daysSinceLastVisit === 1) {
      // Consecutive day
      this.journey.learningStreak.current++;
      this.journey.learningStreak.longest = Math.max(
        this.journey.learningStreak.longest,
        this.journey.learningStreak.current
      );
    } else if (daysSinceLastVisit > 1) {
      // Streak broken
      this.journey.learningStreak.current = 1;
    }

    this.journey.learningStreak.lastVisit = now;
  }

  // ===== RECOMMENDATIONS =====

  getRecommendations(): Recommendation[] {
    const recommendations: Recommendation[] = [];

    // Based on island progress
    const unvisitedRegions = this.getUnvisitedIslandRegions();
    if (unvisitedRegions.length > 0) {
      recommendations.push({
        type: 'page',
        id: '/island',
        title: 'Explore More Island Regions',
        description: `You have ${unvisitedRegions.length} capability regions left to discover`,
        reason: {
          type: 'next-in-sequence',
          context: 'Island exploration',
          confidence: 0.9,
        },
        priority: 8,
      });
    }

    // Based on mountain progress
    const nextElevation = this.getNextMountainElevation();
    if (nextElevation) {
      recommendations.push({
        type: 'page',
        id: '/mountain',
        title: `Ascend to ${nextElevation}`,
        description: 'Continue your DEGAP journey to the next elevation',
        reason: {
          type: 'next-in-sequence',
          context: 'Mountain progression',
          confidence: 0.95,
        },
        priority: 9,
      });
    }

    // Based on recent framework views
    // ... (additional recommendation logic)

    return recommendations.sort((a, b) => b.priority - a.priority);
  }

  private getUnvisitedIslandRegions(): IslandRegion[] {
    const allRegions: IslandRegion[] = [
      'clarity-focus', 'conceptual-precision', 'tool-fluency',
      'stakeholder-intel', 'cultural-reinforce', 'rhythm-ritual',
      'tradeoff-intel', 'cost-delay', 'process-discipline',
      'situational-aware', 'outcome-reflection', 'avoidance-activation',
    ];
    return allRegions.filter(r => !this.journey.islandProgress.visitedRegions.has(r));
  }

  private getNextMountainElevation(): MountainElevation | null {
    const elevations: MountainElevation[] = ['base-camp', 'lower-camp', 'middle-camp', 'high-camp', 'summit'];
    const currentIndex = elevations.indexOf(this.journey.mountainProgress.currentElevation);
    return currentIndex < elevations.length - 1 ? elevations[currentIndex + 1] : null;
  }

  // ===== DATA ACCESS =====

  getJourney(): UserJourney {
    return this.journey;
  }

  getAchievements(): Achievement[] {
    return this.journey.achievements;
  }

  getUnlockedAchievements(): Achievement[] {
    return this.journey.achievements.filter(a => a.unlockedAt);
  }

  getStats(): UserJourney['stats'] {
    try {
      // Update real-time stats
      const now = new Date();
      const startedAt = this.journey.currentSession?.startedAt;
      
      if (startedAt) {
        const startTime = startedAt instanceof Date ? startedAt : new Date(startedAt);
        const sessionDuration = Math.floor((now.getTime() - startTime.getTime()) / 1000);
        this.journey.stats.totalTimeSpent += sessionDuration;
      }
    } catch (error) {
      console.error('Error calculating session duration:', error);
    }

    return this.journey.stats;
  }

  // ===== PERSISTENCE =====

  private saveJourney(): void {
    try {
    const serialized = this.serializeJourney(this.journey);
      const jsonString = JSON.stringify(serialized);
      
      // Try to save, but handle quota exceeded gracefully
      try {
        localStorage.setItem(STORAGE_KEY, jsonString);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing, unrelated to this change
      } catch (storageError: any) {
        if (storageError.name === 'QuotaExceededError' || storageError.code === 22) {
          // Quota exceeded - try to clean up old data
          console.warn('localStorage quota exceeded, attempting cleanup...');
          this.cleanupOldData();
          
          // Try again with cleaned data
          try {
            const cleanedSerialized = this.serializeJourney(this.journey);
            localStorage.setItem(STORAGE_KEY, JSON.stringify(cleanedSerialized));
            console.log('Successfully saved after cleanup');
          } catch (retryError) {
            console.error('Failed to save even after cleanup:', retryError);
            // Don't throw - just log the error so the app continues working
          }
        } else {
          throw storageError;
        }
      }
    } catch (error) {
      console.error('Failed to save journey:', error);
      // Don't throw - allow app to continue without journey tracking
    }
  }
  
  /**
   * Clean up old data to reduce storage usage
   */
  private cleanupOldData(): void {
    // Keep only the last 100 page visits
    if (this.journey.pageVisits.length > 100) {
      this.journey.pageVisits = this.journey.pageVisits
        .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime())
        .slice(0, 100);
    }
    
    // Keep only the last 50 framework interactions
    // (field is `frameworksViewed` on UserJourney — this used to read/write
    // `frameworkInteractions`, which has never existed on the type. That
    // meant this branch always threw when localStorage quota was exceeded
    // (this method's only caller), turning the "clean up and retry" recovery
    // path into an uncaught crash instead.)
    if (this.journey.frameworksViewed.length > 50) {
      this.journey.frameworksViewed = this.journey.frameworksViewed
        .sort((a, b) => b.viewedAt.getTime() - a.viewedAt.getTime())
        .slice(0, 50);
    }

    // NOTE: a "keep only recent sessions (last 30 days)" step used to live
    // here, filtering `this.journey.sessions` by `session.startTime`.
    // UserJourney has no `sessions` array (only a single `currentSession`)
    // and never has in this file's current model — same crash-on-quota-
    // exceeded issue as above. Removed rather than inventing a session-
    // history feature that doesn't exist elsewhere in this model.
  }

  private serializeJourney(journey: UserJourney): SerializedUserJourney {
    return {
      ...journey,
      createdAt: journey.createdAt.toISOString(),
      lastActive: journey.lastActive.toISOString(),
      currentSession: {
        ...journey.currentSession,
        startedAt: journey.currentSession.startedAt.toISOString(),
      },
      aiNavigatorStats: {
        totalQueries: journey.aiNavigatorStats.totalQueries,
        topicsExplored: journey.aiNavigatorStats.topicsExplored,
        helpfulResponses: journey.aiNavigatorStats.helpfulResponses,
        unhelpfulResponses: journey.aiNavigatorStats.unhelpfulResponses,
        firstQuery: journey.aiNavigatorStats.firstQuery ? journey.aiNavigatorStats.firstQuery.toISOString() : undefined,
        lastQuery: journey.aiNavigatorStats.lastQuery ? journey.aiNavigatorStats.lastQuery.toISOString() : undefined,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing, unrelated to this change
      } as any,
      islandProgress: {
        visitedRegions: Array.from(journey.islandProgress.visitedRegions),
        regionDetails: Object.fromEntries(journey.islandProgress.regionDetails),
      },
      mountainProgress: {
        currentElevation: journey.mountainProgress.currentElevation,
        reachedElevations: Array.from(journey.mountainProgress.reachedElevations),
        elevationDetails: Object.fromEntries(journey.mountainProgress.elevationDetails),
      },
      mofProgress: {
        exploredLayers: Array.from(journey.mofProgress.exploredLayers),
        layerDetails: Object.fromEntries(journey.mofProgress.layerDetails),
      },
      learningStreak: {
        ...journey.learningStreak,
        lastVisit: journey.learningStreak.lastVisit.toISOString(),
      },
    };
  }

  private deserializeJourney(serialized: SerializedUserJourney): UserJourney {
    try {
      return {
        ...serialized,
        createdAt: new Date(serialized.createdAt),
        lastActive: new Date(serialized.lastActive),
        currentSession: {
          ...serialized.currentSession,
          startedAt: new Date(serialized.currentSession?.startedAt || Date.now()),
        },
        pageVisits: (serialized.pageVisits || []).map(v => ({
          ...v,
          timestamp: new Date(v.timestamp),
        })),
        frameworksViewed: (serialized.frameworksViewed || []).map(f => ({
          ...f,
          viewedAt: new Date(f.viewedAt),
        })),
        glossaryTermsLearned: (serialized.glossaryTermsLearned || []).map(g => ({
          ...g,
          viewedAt: new Date(g.viewedAt),
        })),
        aiNavigatorStats: {
          ...(serialized.aiNavigatorStats || { totalQueries: 0, topicsExplored: [], helpfulResponses: 0, unhelpfulResponses: 0 }),
          firstQuery: serialized.aiNavigatorStats?.firstQuery ? new Date(serialized.aiNavigatorStats.firstQuery) : undefined,
          lastQuery: serialized.aiNavigatorStats?.lastQuery ? new Date(serialized.aiNavigatorStats.lastQuery) : undefined,
        },
        islandProgress: {
          visitedRegions: new Set(serialized.islandProgress?.visitedRegions || []),
          // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing, unrelated to this change
          regionDetails: new Map(Object.entries(serialized.islandProgress?.regionDetails || {})) as any,
        },
        mountainProgress: {
          currentElevation: serialized.mountainProgress?.currentElevation || null,
          reachedElevations: new Set(serialized.mountainProgress?.reachedElevations || []),
          // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing, unrelated to this change
          elevationDetails: new Map(Object.entries(serialized.mountainProgress?.elevationDetails || {})) as any,
        },
        mofProgress: {
          exploredLayers: new Set(serialized.mofProgress?.exploredLayers || []),
          // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing, unrelated to this change
          layerDetails: new Map(Object.entries(serialized.mofProgress?.layerDetails || {})) as any,
        },
        learningStreak: {
          ...(serialized.learningStreak || { current: 0, longest: 0 }),
          lastVisit: new Date(serialized.learningStreak?.lastVisit || Date.now()),
        },
      };
    } catch (error) {
      console.error('Error deserializing journey, creating new journey:', error);
      return this.createNewJourney();
    }
  }

  // Reset for testing/demo purposes
  reset(): void {
    localStorage.removeItem(STORAGE_KEY);
    this.journey = this.createNewJourney();
    this.saveJourney();
  }
}

// Singleton instance
export const journeyTracker = new JourneyTracker();

